import {Loader2} from 'lucide-react';
import {
  AttioIcon,
  Card,
  HubspotIcon,
  SalesforceIcon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
} from '@tinct/web-components';
import {AttioIntegration, HubspotIntegration, SalesforceIntegration} from '@tinct/web-feature-organizations';
import {CrmProviderType, CrmSegment} from '@tinct/core-api-models';
import {cn} from '@tinct/core-utils';
import {type CrmProviderOption} from '../hooks/use-crm-import';
import {type ReactNode} from 'react';

type CrmImportContentProps = {
  // Provider selection
  providers: CrmProviderOption[];
  selectedProvider: CrmProviderType | null;
  onProviderChange: (provider: CrmProviderType) => void;
  selectedProviderOption: CrmProviderOption | null;

  // Connection state
  isLoadingConnection: boolean;

  // Segments
  segments: CrmSegment[];
  isLoadingSegments: boolean;
  selectedSegment: CrmSegment | null;
  onSegmentSelect: (segment: CrmSegment) => void;
};

// Partial: rudderstack is intentionally unsupported in CRM import, so it has
// no config entry. Access sites already guard for an undefined config.
const CRM_PROVIDER_CONFIG: Partial<Record<CrmProviderType, {
  label: string;
  icon: (size: number) => ReactNode;
}>> = {
  hubspot: {
    label: 'Hubspot',
    icon: (size) => <HubspotIcon width={size} height={size} />,
  },
  attio: {
    label: 'Attio',
    icon: (size) => <AttioIcon width={size} height={size} />,
  },
  salesforce: {
    label: 'Salesforce',
    icon: (size) => <SalesforceIcon width={size} height={size} />
  }
};

function ConnectionStatusBadge({ isConnected }: { isConnected: boolean }) {
  return (
    <div className="flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5">
      <span
        className={cn(
          'size-2 rounded-full',
          isConnected ? 'bg-emerald-400' : 'bg-rose-500'
        )}
      />
      <span className="text-xs font-medium text-secondary-foreground">
        {isConnected ? 'Connected' : 'Not connected'}
      </span>
    </div>
  );
}

function CrmProviderSelect({
  providers,
  selectedProvider,
  onProviderChange,
}: {
  providers: CrmProviderOption[];
  selectedProvider: CrmProviderType | null;
  onProviderChange: (provider: CrmProviderType) => void;
}) {
  return (
    <Select
      value={selectedProvider ?? undefined}
      onValueChange={(value) => onProviderChange(value as CrmProviderType)}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select a CRM">
          {selectedProvider && CRM_PROVIDER_CONFIG[selectedProvider] && (
            <span className="flex items-center gap-2">
              {CRM_PROVIDER_CONFIG[selectedProvider].icon(20)}
              <span className="font-medium">{CRM_PROVIDER_CONFIG[selectedProvider].label}</span>
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {providers.map((option) => {
          const config = CRM_PROVIDER_CONFIG[option.provider];
          if (!config) return null;
          return (
            <SelectItem key={option.provider} value={option.provider}>
              <div className="flex w-full items-center gap-2">
                {config.icon(20)}
                <span className="flex-1 font-medium">{config.label}</span>
                <ConnectionStatusBadge isConnected={option.isConnected} />
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function SegmentRow({
  segment,
  isSelected,
  onSelect,
}: {
  segment: CrmSegment;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 px-6 py-3 text-left hover:bg-muted/50 transition-colors"
    >
      <div
        className={cn(
          'size-4 shrink-0 rounded-full border',
          isSelected
            ? 'border-primary bg-primary'
            : 'border-border bg-background shadow-xs'
        )}
      >
        {isSelected && (
          <div className="flex size-full items-center justify-center">
            <div className="size-2 rounded-full bg-primary-foreground" />
          </div>
        )}
      </div>
      <span className="flex-1 text-sm font-medium">{segment.name}</span>
      <span className="text-sm text-muted-foreground">{segment.memberCount}</span>
    </button>
  );
}

function SegmentsList({
  segments,
  selectedSegment,
  onSegmentSelect,
  isLoading,
}: {
  segments: CrmSegment[];
  selectedSegment: CrmSegment | null;
  onSegmentSelect: (segment: CrmSegment) => void;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="w-full">
        <div className="flex flex-col gap-1 py-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-3">
              <Skeleton className="size-4 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-8" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (segments.length === 0) {
    return (
      <Card className="flex h-[200px] w-full items-center justify-center">
        <p className="text-sm text-muted-foreground">No segments found in your CRM</p>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-1 py-1">
        {segments.map((segment, index) => (
          <div key={segment.id}>
            <SegmentRow
              segment={segment}
              isSelected={selectedSegment?.id === segment.id}
              onSelect={() => onSegmentSelect(segment)}
            />
            {index < segments.length - 1 && <Separator />}
          </div>
        ))}
      </div>
    </Card>
  );
}

const INTEGRATION_COMPONENTS: Partial<Record<CrmProviderType, () => ReactNode>> = {
  hubspot: () => <HubspotIntegration />,
  attio: () => <AttioIntegration />,
  salesforce: () => <SalesforceIntegration />,
};

function NotConnectedState({ provider }: { provider: CrmProviderType }) {
  const config = CRM_PROVIDER_CONFIG[provider];
  const IntegrationComponent = INTEGRATION_COMPONENTS[provider];

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        Connect your {config?.label} account
      </p>
      {IntegrationComponent ? (
        <div className="[&>div]:max-w-none">
          {IntegrationComponent()}
        </div>
      ) : (
        <Card className="flex h-[200px] w-full items-center justify-center">
          <p className="text-sm text-muted-foreground">
            {config?.label} integration is not available yet.
          </p>
        </Card>
      )}
    </div>
  );
}

function ConnectedState({
  segments,
  selectedSegment,
  onSegmentSelect,
  isLoadingSegments,
}: {
  segments: CrmSegment[];
  selectedSegment: CrmSegment | null;
  onSegmentSelect: (segment: CrmSegment) => void;
  isLoadingSegments: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">Select a list</p>
      <SegmentsList
        segments={segments}
        selectedSegment={selectedSegment}
        onSegmentSelect={onSegmentSelect}
        isLoading={isLoadingSegments}
      />
    </div>
  );
}

export const CrmImportContent = ({
  providers,
  selectedProvider,
  onProviderChange,
  selectedProviderOption,
  isLoadingConnection,
  segments,
  isLoadingSegments,
  selectedSegment,
  onSegmentSelect,
}: CrmImportContentProps) => {
  if (isLoadingConnection) {
    return (
      <div className="flex h-[280px] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Section 1: CRM Provider Selector */}
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">Select a CRM</p>
        <CrmProviderSelect
          providers={providers}
          selectedProvider={selectedProvider}
          onProviderChange={onProviderChange}
        />
      </div>

      {/* Section 2: Segments list or Integration card */}
      {selectedProviderOption && (
        selectedProviderOption.isConnected ? (
          <ConnectedState
            segments={segments}
            selectedSegment={selectedSegment}
            onSegmentSelect={onSegmentSelect}
            isLoadingSegments={isLoadingSegments}
          />
        ) : (
          <NotConnectedState provider={selectedProviderOption.provider} />
        )
      )}
    </div>
  );
};
